import React, { useRef, useState, useEffect } from "react";
import styles from "./VasekBuchtaPage.module.css";
import emailjs from 'emailjs-com';
import { FaEnvelope, FaPhone } from 'react-icons/fa';

const accentColor = "#14b8a6";

// EmailJS configuration
const EMAILJS_SERVICE_ID = 'service_bvnhuhe'; // Replace with your EmailJS service ID
const EMAILJS_TEMPLATE_ID = 'template_55hijvn'; // Replace with your EmailJS template ID
const EMAILJS_USER_ID = '-Vs-y6I_NiyGR5Nc5'; // Replace with your EmailJS user ID

// Helper for active menu link
const useActiveSection = (ids: string[]) => {
  const [active, setActive] = useState(ids[0]);
  useEffect(() => {
    const onScroll = () => {
      const scrollY = window.scrollY;
      let current = ids[0];
      for (const id of ids) {
        const el = document.getElementById(id);
        if (el && el.offsetTop - 80 <= scrollY) {
          current = id;
        }
      }
      setActive(current);
    };
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, [ids]);
  return active;
};

// Fix for react-icons TypeScript issue
const EnvelopeIcon = FaEnvelope as unknown as React.FC<React.SVGProps<SVGSVGElement>>;
const PhoneIcon = FaPhone as unknown as React.FC<React.SVGProps<SVGSVGElement>>;

const VasekBuchtaPage: React.FC = () => {
  const [copied, setCopied] = useState(false);
  const [showScroll, setShowScroll] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const email = "buchta.vaclav@seznam.cz";
  const scrollRef = useRef<HTMLDivElement>(null);

  const sectionIds = [
    "sluzby",
    "skoly",
    "poradenstvi",
    "omne",
    "kontakt",
  ];
  const sectionLabels = [
    "Pro sociální služby",
    "Pro školy",
    "Poradenství a konzultace",
    "O mně",
    "Kontakt",
  ];
  const activeSection = useActiveSection(sectionIds);

  // Contact form state
  const [form, setForm] = useState({ name: '', email: '', message: '', robotAnswer: '', topic: '', meetingType: 'chat', preferredTime: '' });
  const [formError, setFormError] = useState<string | null>(null);
  const [formSuccess, setFormSuccess] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const robotQuestion = '3 + 4 = ?';
  const robotSolution = '7';

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const [showCookies, setShowCookies] = useState(() => {
    if (typeof window !== 'undefined') {
      return !localStorage.getItem('cookiesAccepted');
    }
    return true;
  });

  const handleCookiesAccept = () => {
    localStorage.setItem('cookiesAccepted', '1');
    setShowCookies(false);
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setFormError(null);
    setFormSuccess(null);
  };

  const validateForm = () => {
    if (!form.name.trim() || !form.email.trim() || !form.topic.trim() || !form.preferredTime.trim()) {
      setFormError('Vyplňte prosím všechna povinná pole.');
      return false;
    }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) {
      setFormError('Zadejte platný e-mail.');
      return false;
    }
    if (form.robotAnswer.trim() !== robotSolution) {
      setFormError('Odpověď na kontrolní otázku je nesprávná.');
      return false;
    }
    return true;
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    setFormSuccess(null);
    
    if (!validateForm()) return;
    
    setSending(true);
    
    try {
      const templateParams = {
        from_name: form.name,
        from_email: form.email,
        topic: form.topic,
        meeting_type: form.meetingType === 'chat' ? 'Chat' : 
                     form.meetingType === 'online' ? 'Online hovor' : 
                     'Osobní schůzka',
        preferred_time: form.preferredTime,
        message: form.message || 'Žádná doplňující zpráva',
      };

      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        templateParams,
        EMAILJS_USER_ID
      );

      setFormSuccess('Zpráva byla úspěšně odeslána. Děkuji!');
      setForm({ 
        name: '', 
        email: '', 
        message: '', 
        robotAnswer: '', 
        topic: '', 
        meetingType: 'chat', 
        preferredTime: '' 
      });
    } catch (error) {
      console.error('Error sending email:', error);
      setFormError('Odeslání selhalo. Zkuste to prosím později nebo použijte alternativní způsob kontaktu.');
    } finally {
      setSending(false);
    }
  };

  // Copy email to clipboard
  const handleCopy = () => {
    navigator.clipboard.writeText(email);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  // Show scroll-to-top button
  useEffect(() => {
    const onScroll = () => {
      setShowScroll(window.scrollY > 200);
    };
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Scroll to top
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Smooth scroll to section
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offsetTop = element.offsetTop - 80; // Account for fixed header
      window.scrollTo({
        top: offsetTop,
        behavior: "smooth"
      });
    }
  };

  return (
    <div className={styles.pageBg} ref={scrollRef}>
      <div className={styles.mainWrapper}>
        {/* Menu */}
        <nav className={styles.menu} role="navigation" aria-label="Hlavní navigace">
          {/* Hamburger icon for mobile */}
          <button
            className={`${styles.hamburger} ${mobileMenuOpen ? styles.menuOpen : ''}`}
            aria-label={mobileMenuOpen ? 'Zavřít menu' : 'Otevřít menu'}
            aria-expanded={mobileMenuOpen}
            onClick={() => setMobileMenuOpen((open) => !open)}
            type="button"
          >
            <span className={styles.hamburgerBar} />
            <span className={styles.hamburgerBar} />
            <span className={styles.hamburgerBar} />
          </button>
          {/* Desktop menu */}
          <ul className={styles.menuList}>
            {sectionIds.map((id, i) => (
              <li key={id}>
                <a
                  href={`#${id}`}
                  className={
                    activeSection === id
                      ? `${styles.menuLink} ${styles.menuLinkActive}`
                      : styles.menuLink
                  }
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToSection(id);
                  }}
                >
                  {sectionLabels[i]}
                </a>
              </li>
            ))}
          </ul>
          {/* Mobile dropdown menu */}
          <ul
            className={
              styles.mobileMenu +
              (mobileMenuOpen ? ' ' + styles.mobileMenuOpen : '')
            }
            onClick={() => setMobileMenuOpen(false)}
          >
            {sectionIds.map((id, i) => (
              <li key={id}>
                <a
                  href={`#${id}`}
                  className={
                    activeSection === id
                      ? `${styles.menuLink} ${styles.menuLinkActive}`
                      : styles.menuLink
                  }
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToSection(id);
                    setMobileMenuOpen(false);
                  }}
                >
                  {sectionLabels[i]}
                </a>
              </li>
            ))}
          </ul>
        </nav>
        
        <main className={styles.vbContainer + " vb-container"}>
          <header className={styles.headerFooter}>
            <div className={styles.introHeader}>
              <img
                src={require('./assets/images/vaclav-buchta-4097.jpg')}
                alt="Václav Buchta portrét"
                className={styles.introAvatar}
              />
              <div className={styles.introText}>
                <h1 className={styles.vbH1 + " vb-h1"}>Václav Buchta</h1>
                <p className={styles.headerServices}><span>odborné konzultace pro sociální služby, preventivní přednášky, osobní konzultace a poradenství</span></p>
              </div>
            </div>
          </header>

          <article id="sluzby" className={styles.headerFooter} style={{marginBottom: '7rem'}}>
            <h2 className={styles.vbH2 + " vb-h2"}>Pro sociální služby</h2>
            <div style={{height: 2, background: 'rgba(20,184,166,0.10)', borderRadius: 2, margin: '0 0 2.2rem 0', width: '100%'}} />
            <h3 className={styles.vbH3 + " vb-h3"}>Vzdělávání na míru pro pracovníky v pomáhajících profesích</h3>
            <p className={styles.vbP + " vb-p"}>
              Prakticky zaměřené kurzy vycházející z každodenní reality sociálních služeb a práce s lidmi s duševním onemocněním, závislostí či duální diagnózou.
            </p>
            <p className={styles.vbP + " vb-p"}>
              Témata připravuji na základě zkušeností z terénu i podle konkrétních potřeb vaší organizace.
            </p>
            <h3 className={styles.vbH3 + " vb-h3"}>Vybrané oblasti vzdělávání:</h3>
            <ul className={styles.vbUl + " vb-ul"}>
              <li>Jak efektivně komunikovat s lidmi s duševním onemocněním</li>
              <li>Podpora lidí s duální diagnózou v praxi – přístupy, možnosti, limity</li>
              <li>Peer konzultanti ve službách – jak je zapojit a udržet spolupráci</li>
            </ul>
            <p className={styles.vbP + " vb-p"}>
              Kurzy jsou stavěné na míru konkrétním týmům a situacím v praxi. Nabízejí vysokou míru flexibility, důraz na praktičnost a možnost přizpůsobení témat i formy podle potřeb vaší organizace. Vhodné jsou zejména tam, kde je cílem adresné, obsahově cílené vzdělávání. Kurzy nejsou akreditované.
            </p>
            <h3 className={styles.vbH3 + " vb-h3"}>Metodická podpora:</h3>
            <ul className={styles.vbUl + " vb-ul"}>
              <li>Při zapojování peer konzultantů do služby</li>
              <li>Při rozvoji práce s lidmi s duální diagnózou a case managementu</li>
            </ul>
            <p className={styles.vbP + " vb-p"}>
              Nabízím odborné konzultace pro sociální služby a projekty, které se věnují podobné problematice. Konzultace může být jednorázová i opakovaná – podle potřeb konkrétního týmu nebo situace. Může probíhat osobně nebo online a její obsah i forma se domlouvají individuálně „na míru".
            </p>
            <p className={styles.vbPrice + " vb-price"}>
              <em>
                Cena kurzu je 10 000 za vzdělávací den.<br />
                Cena za online konzultaci je 750 Kč za 1 hodinu.<br />
                Cena za osobní konzultaci se stanovuje individuálně podle místa, kde probíhá. V případě cest mimo Brno se připočítávají i náklady na dopravu.<br />
                Délku konzultace, metodické podpory, kurzu si můžete zvolit dle Vašich potřeb.
              </em>
            </p>
          </article>

          <article id="skoly" className={styles.headerFooter} style={{marginBottom: '7rem'}}>
            <h2 className={styles.vbH2 + " vb-h2"}>Pro školy</h2>
            <div style={{height: 2, background: 'rgba(20,184,166,0.10)', borderRadius: 2, margin: '0 0 2.2rem 0', width: '100%'}} />
            <h3 className={styles.vbH3 + " vb-h3"}>Preventivní přednáška pro žáky</h3>
            <p className={styles.vbP + " vb-p"}>
              Nabízím preventivní přednášku zaměřenou na problematiku návykových látek a sociálně patologických jevů. Je určena především pro žáky 9. tříd základních škol a 1. ročníků středních škol a odborných učilišť. Přednáška vznikala a byla postupně rozvíjena více než 12 let. Zaměřuji se v ní mimo jiné na:
            </p>
            <ul className={styles.vbUl + " vb-ul"}>
              <li>motivace ke zneužívání návykových látek,</li>
              <li>význam smysluplného volného času jako součásti prevence duševního zdraví,</li>
              <li>roli rodiny, výchovné tendence a možná rizika,</li>
              <li>význam komunikace v rodině.</li>
            </ul>
            <h3 className={styles.vbH3 + " vb-h3"}>Preventivní přednáška pro rodiče žáků a studentů</h3>
            <p className={styles.vbP + " vb-p"}>
              Nabízím také přednášku určenou rodičům. Přednáška může být koncipována pouze pro rodiče, ale ukazuje se, že je výhodnější ji uspořádat pro rodiče i žáky a studenty (2 oddělené přednášky). Díky tomu, že se stejným tématům věnujeme jak s dětmi, tak s jejich rodiči, se prevence stává ucelenější a účinnější. Podporuje to následné rozhovory v rodinách a zvyšuje se šance, že si rodina osvojí konkrétní postupy a bude je aktivně využívat v každodenním životě. Přednáška může být součástí rodičovských schůzek nebo může být naplánována školou dle jejích potřeb.
            </p>
            <p className={styles.vbPrice + " vb-price"}><em>Délka přednášky je 90 minut, cena je 1 500 Kč.</em></p>
          </article>

          <article id="poradenstvi" className={styles.headerFooter} style={{marginBottom: '7rem'}}>
            <h2 className={styles.vbH2 + " vb-h2"}>Poradenství a konzultace</h2>
            <div style={{height: 2, background: 'rgba(20,184,166,0.10)', borderRadius: 2, margin: '0 0 2.2rem 0', width: '100%'}} />
            <img
              src={require('./assets/images/vaclav-buchta-4124.jpg')}
              alt="Václav Buchta - poradenství a konzultace"
              className={styles.poradImageFloat}
            />
            <p className={styles.vbP + " vb-p"}>
              Nabízím poradenství a konzultace zaměřené na odvykání návykových látek, zvládání problémů s gamblingem, podporu duševního zdraví a prevenci v těchto oblastech. Pomoc může být určena přímo Vám, nebo se také můžete poradit o svých blízkých. Nejedná se o psychoterapii ani o dlouhodobé provázení v rámci sociálních služeb. Cílem je poskytnout srozumitelnou podporu a orientaci při hledání řešení. Ne každý potřebuje sociální služby nebo terapii – někdy stačí jednorázová konzultace k tomu, aby si člověk ujasnil další kroky. Konzultace však může být i opakovaná podle Vaší potřeby. Setkání může proběhnout osobně, případně online formou videohovoru nebo prostřednictvím chatu (psanou komunikací). Pokud by Vám více vyhovovalo osobní setkání u Vás – například kvůli pohodlí, soukromí nebo zapojení dalších členů rodiny – mohu přijet za Vámi.
            </p>
             <p className={styles.vbPrice + " vb-price"}><em>Cena za konzultaci je 600 Kč za 1 hodinu.</em></p>
          </article>

          <article id="omne" className={styles.headerFooter} style={{marginBottom: '7rem'}}>
            <h2 className={styles.vbH2 + " vb-h2"}>O mně</h2>
            <div style={{height: 2, background: 'rgba(20,184,166,0.10)', borderRadius: 2, margin: '0 0 2.2rem 0', width: '100%'}} />
            <p className={styles.vbP + " vb-p"}>
              V oblasti sociální práce se pohybuji již více než 18 let. Mám zkušenosti z různých pozic v sociálních službách, zejména <strong>v oblasti podpory bydlení pro osoby s duševním onemocněním, závislostí, duální diagnózou a často také v kontextu bezdomovectví.</strong>
            </p>
            <p className={styles.vbP + " vb-p"}>
              Kromě přímé práce s klienty a vedení týmů se dlouhodobě věnuji také <strong>lektorské činnosti</strong>. <strong>Přednáším a vedu kurzy pro odbornou veřejnost a pracovníky v pomáhajících profesích.</strong> Mezi hlavní témata mé lektorské práce patří <strong>komunikace s lidmi s duševním onemocněním, case management nebo práce s klienty s duální diagnózou.</strong>
            </p>
            <p className={styles.vbP + " vb-p"}>
              <strong>Zaměřuji se rovněž na metodickou podporu týmů</strong> – zejména <strong>při zavádění pozice peer konzultanta, rozvoji case managementu a práci s lidmi se závislostním chováním.</strong> Individuálně pracuji i s klienty online, kteří se snaží o <strong>změnu ve vztahu k užívání marihuany nebo hazardnímu hraní.</strong>
            </p>
            <p className={styles.vbP + " vb-p"}>
              Spolupracuji nebo jsem spolupracoval s řadou organizací: <strong>Práh jižní Morava</strong> (15 let), <strong>Centrum pro rozvoj péče o duševní zdraví</strong> (3 roky), <strong>Sananim</strong> (16 let), <strong>Podané ruce</strong> (4 roky), <strong>Národní centrum ošetřovatelství a nelékařských oborů</strong> (7 let), <strong>SŠTE Olomoucká</strong> (12 let) nebo <strong>Renadi</strong> (3 roky).
            </p>
            <p className={styles.vbP + " vb-p"}>
              Vystudoval VOŠ EA Sociálně právní v Brně (2007), nyní si doplňuji vzdělání v bakalářském programu – CMTF UP a CARITAS v Olomouci. Ve své profesi se nadále pravidelně vzdělávám a sleduji aktuální trendy v sociální práci.
            </p>
            <p className={styles.vbP + " vb-p"}><em><strong>Mezi absolvované kurzy patří například:</strong></em></p>
            <ul className={styles.vbUl + " vb-ul"}>
              <li>Motivační rozhovory (60 hodin)</li>
              <li>Úvod do teorie a praxe Otevřeného dialogu</li>
              <li>Cooperation – rozvíjení spolupráce v týmech a organizacích</li>
              <li>Recovery model – úvod i aplikace v praxi</li>
              <li>Otevřený dialog v praxi (Prof. Jaakko Seikkula, Ph.D.)</li>
              <li>GAF – Global Assessment of Functioning</li>
              <li>Aktuální trendy v případové práci a case managementu</li>
              <li>Zapojení peer konzultanta do týmů sociálních služeb</li>
              <li>Zvládání obtížných situací při spolupráci s klientem</li>
              <li>Ochrana práv uživatelů sociálních služeb</li>
              <li>Závislost – výzkum, praxe a pacienti. Tři rozdílné světy</li>
              <li>Opatrovnictví, rozhodování lidí s duševním postižením a právní rámec</li>
              <li>Práce s rodinami osob s duševním onemocněním</li>
              <li>Psychosociální rehabilitace STORM</li>
              <li>Psychiatrické minimum</li>
              <li>Konference komunitní psychiatrie 2007, 2009, 2016, 2018</li>
              <li>Konference Recovery 2020, 2024</li>
              <li>…a další témata související se sociální prací, duševním zdravím a podporou zotavení.</li>
            </ul>
          </article>

          <section id="kontakt" className={styles.headerFooter} style={{marginBottom: '7rem'}}>
            <h2 className={styles.vbH2 + " vb-h2"}>Kontaktovat mě můžete telefonicky, smskou, emailem, nebo vyplněným formulářem, který nám může navázání kontaktu zjednodušit.</h2>
            <div style={{height: 2, background: 'rgba(20,184,166,0.10)', borderRadius: 2, margin: '0 0 2.2rem 0', width: '100%'}} />
            <address className={styles.contactFlex}>
              <div className={styles.contactInfo}>
                <p className={styles.vbP + " vb-p"}>
                  <strong>Václav Buchta, DiS.</strong><br />
                  <em>lektor, konzultant v oblasti sociálních služeb</em><br />
                  <PhoneIcon style={{ marginRight: 8, verticalAlign: 'middle', color: accentColor }} /> <a href="tel:+420737789324" className={styles.vbEmail + " vb-email"}>+420 737 789 324</a><br />
                  <EnvelopeIcon style={{ marginRight: 8, verticalAlign: 'middle', color: accentColor }} /> <a className={styles.vbEmail + " vb-email"} href="mailto:buchta.vaclav@seznam.cz">{email}</a>
                  <button className={styles.copyBtn} onClick={handleCopy}>Kopírovat</button>
                  {copied && <span className={styles.copiedMsg}>Zkopírováno!</span>}<br />
                  IČ: 19822448
                  <br />
                  <strong><em>Kde mě najdete:</em></strong><br />
                  <div className={styles.mapContainer}>
                    <iframe
                      src="https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=Gorkého+54/4,+602+00+Brno,+Czech+Republic"
                      allowFullScreen
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title="Mapa - Gorkého 54/4, Brno"
                    ></iframe>
                  </div>
                </p>
                <button 
                  className={styles.toggleFormBtn} 
                  onClick={() => setShowForm(!showForm)}
                  aria-expanded={showForm}
                >
                  {showForm ? 'Skrýt kontaktní formulář' : 'Zobrazit kontaktní formulář'}
                </button>
              </div>
            </address>
            <div className={`${styles.contactFormWrapper} ${showForm ? styles.contactFormVisible : ''}`}>
              <form className={styles.contactForm} onSubmit={handleFormSubmit} autoComplete="off">
                <div className={styles.formGroup}>
                  <label className={styles.formLabel} htmlFor="name">Jméno</label>
                  <input className={styles.formInput} type="text" id="name" name="name" value={form.name} onChange={handleFormChange} disabled={sending} required />
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel} htmlFor="email">E-mail</label>
                  <input className={styles.formInput} type="email" id="email" name="email" value={form.email} onChange={handleFormChange} disabled={sending} required />
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel} htmlFor="topic">
                    1. S čím byste se chtěli poradit?
                    <span className={styles.formSubtext}>Stručně popište téma nebo otázku, kterou byste rádi probrali.</span>
                  </label>
                  <textarea className={styles.formTextarea} id="topic" name="topic" rows={3} value={form.topic} onChange={handleFormChange} disabled={sending} required />
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>
                    2. Jaký způsob setkání Vám nejlépe vyhovuje?
                  </label>
                  <div className={styles.radioGroup}>
                    <label className={styles.radioLabel}>
                      <input
                        type="radio"
                        name="meetingType"
                        value="chat"
                        checked={form.meetingType === 'chat'}
                        onChange={handleFormChange}
                        disabled={sending}
                        required
                      />
                      <span>Chat</span>
                    </label>
                    <label className={styles.radioLabel}>
                      <input
                        type="radio"
                        name="meetingType"
                        value="online"
                        checked={form.meetingType === 'online'}
                        onChange={handleFormChange}
                        disabled={sending}
                      />
                      <span>Online hovor</span>
                    </label>
                    <label className={styles.radioLabel}>
                      <input
                        type="radio"
                        name="meetingType"
                        value="personal"
                        checked={form.meetingType === 'personal'}
                        onChange={handleFormChange}
                        disabled={sending}
                      />
                      <span>Osobní schůzka v místě podle Vašeho výběru</span>
                    </label>
                  </div>
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel} htmlFor="preferredTime">
                    3. Kdy byste měli čas na setkání?
                    <span className={styles.formSubtext}>Napište, prosím, kdy by se Vám to v nejbližších dnech hodilo – ideálně více možností.</span>
                  </label>
                  <textarea className={styles.formTextarea} id="preferredTime" name="preferredTime" rows={3} value={form.preferredTime} onChange={handleFormChange} disabled={sending} required />
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel} htmlFor="message">Doplňující informace</label>
                  <textarea className={styles.formTextarea} id="message" name="message" rows={3} value={form.message} onChange={handleFormChange} disabled={sending} />
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel} htmlFor="robotAnswer">Ověření: <span className={styles.robotCheck}>{robotQuestion}</span></label>
                  <input className={styles.formInput} type="text" id="robotAnswer" name="robotAnswer" value={form.robotAnswer} onChange={handleFormChange} disabled={sending} required autoComplete="off" />
                </div>
                {formError && <div className={styles.formError}>{formError}</div>}
                {formSuccess && <div className={styles.formSuccess}>{formSuccess}</div>}
                <button className={styles.formButton} type="submit" disabled={sending}>{sending ? 'Odesílám...' : 'Odeslat zprávu'}</button>
              </form>
            </div>
          </section>

          <footer className={styles.footerText}>
            <div className={styles.footerBottom}>
              <p>&copy; {new Date().getFullYear()} Václav Buchta</p>
            </div>
          </footer>
        </main>
        {showScroll && (
          <button className={styles.scrollTopBtn} onClick={scrollToTop} title="Zpět nahoru" aria-label="Zpět nahoru">
            ↑
          </button>
        )}
      </div>
      {showCookies && (
        <div style={{
          position: 'fixed',
          bottom: 24,
          left: 0,
          right: 0,
          margin: '0 auto',
          maxWidth: 420,
          background: 'rgba(255,255,255,0.98)',
          color: '#222',
          borderRadius: 14,
          boxShadow: '0 4px 24px rgba(20,184,166,0.13)',
          padding: '1.1rem 1.5rem 1.1rem 1.5rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          zIndex: 2000,
          fontSize: 15,
        }}>
          <span style={{marginRight: 16}}>
            Tento web používá cookies pro zajištění správné funkce a analýzu návštěvnosti. Používáním webu s tím souhlasíte.
          </span>
          <button
            onClick={handleCookiesAccept}
            style={{
              background: accentColor,
              color: '#fff',
              border: 'none',
              borderRadius: 7,
              padding: '0.5rem 1.1rem',
              fontWeight: 600,
              fontSize: 15,
              cursor: 'pointer',
              marginLeft: 8,
              boxShadow: '0 2px 8px rgba(20,184,166,0.07)',
              transition: 'background 0.2s',
            }}
          >
            Rozumím
          </button>
        </div>
      )}
    </div>
  );
};

export default VasekBuchtaPage;
